import React, { useState, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import axios from "axios";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import { localApi, credentials } from "utils/imgApi";
import styles from "assets/jss/material-dashboard-pro-react/views/registerPageStyle";

const useStyles = makeStyles(styles);

// Helper function to trigger file download
const triggerDownload = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export default function CsvFileUpload() {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  const fileInputRef1 = useRef(null);

  // State for CSV Filter (getCSV endpoint)
  const [file1, setFile1] = useState(null);
  const [loading1, setLoading1] = useState(false);
  const [message1, setMessage1] = useState("");

  // State for CSV Upload (uploadCSV endpoint)
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [rejectedFileName, setRejectedFileName] = useState("");

  // Handle file selection for CSV Filter
  const handleFileChange1 = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.toLowerCase().endsWith(".csv")) {
        setMessage1("Please select a valid CSV file.");
        setFile1(null);
        return;
      }
      setFile1(selectedFile);
      setMessage1("");
    }
  };

  // Handle upload for CSV Filter (getCSV endpoint)
  const handleUpload1 = async () => {
    if (!file1) {
      setMessage1("No file selected.");
      return;
    }

    setLoading1(true);
    setMessage1("");

    const formData = new FormData();
    formData.append("file", file1);

    try {
      const res = await fetch(`${localApi}/contact/getCSV`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Basic ${credentials}`,
        },
      });

      const contentType = res.headers.get("Content-Type") || "";

      if (
        contentType.includes("text/csv") ||
        contentType.includes("application/octet-stream")
      ) {
        const blob = await res.blob();

        // Extract filename from Content-Disposition header
        let fileName = "clean_contacts.csv";
        const contentDisposition = res.headers.get("Content-Disposition");
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(
            /filename\*?="?([^;"]+)"?/i,
          );
          if (filenameMatch && filenameMatch[1]) {
            try {
              fileName = decodeURIComponent(filenameMatch[1].trim());
            } catch {
              fileName = filenameMatch[1].trim();
            }
          }
        }

        triggerDownload(blob, fileName);
        setMessage1("Filtered CSV file has been Downloaded Successfully.");
      } else {
        const json = await res.json();
        setMessage1(
          json.message ||
            "No clean records found or an unexpected response was received.",
        );
      }
    } catch (err) {
      console.error("Upload error:", err);
      setMessage1("Failed to process file. Please try again.");
    } finally {
      setLoading1(false);
    }
  };

  // Handle file selection for CSV Upload
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];

    if (
      selectedFile &&
      selectedFile.type !== "text/csv" &&
      !selectedFile.name.toLowerCase().endsWith(".csv")
    ) {
      setMessage("Please select a valid CSV file");
      setMessageType("error");
      setFile(null);
      setDownloadUrl(null);
      setRejectedFileName("");
      return;
    }

    setFile(selectedFile);
    setRejectedFileName(selectedFile ? `rejected_${selectedFile.name}` : "");
    setMessage("");
    setMessageType("");
    setDownloadUrl(null);
  };

  // Handle upload for CSV Upload (uploadCSV endpoint)
  const handleUpload = async () => {
    if (!file) {
      setMessage("Please select a file");
      setMessageType("error");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);

    try {
      const response = await axios.post(
        `${localApi}/contact/uploadCSV`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Basic ${credentials}`,
            Accept: "application/json",
          },
        },
      );

      const successCount = response.data.successCount || 0;
      const rejectedCount = response.data.rejectedCount || 0;

      setMessage(`Success! ${successCount} records imported.`);
      setMessageType("success");

      // Handle rejected records download
      if (rejectedCount > 0) {
        if (response.data.rejectedCSV) {
          const blob = new Blob([response.data.rejectedCSV], {
            type: "text/csv",
          });
          const url = window.URL.createObjectURL(blob);
          setDownloadUrl(url);
          setRejectedFileName(`rejected_${file.name}`);
        } else if (response.data.rejectedFileUrl) {
          setDownloadUrl(response.data.rejectedFileUrl);
          setRejectedFileName(`rejected_${file.name}`);
        }
      }
      // REMOVED: Auto-refresh code that was causing issues
      // The page will no longer refresh automatically
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "Upload failed";
      setMessage(`Upload failed: ${errorMessage}`);
      setMessageType("error");
      console.error("Upload error:", error);
    } finally {
      setUploading(false);
    }
  };

  // Handle download of rejected records
  const handleDownload = () => {
    if (downloadUrl) {
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download =
        rejectedFileName || `rejected_records_${new Date().getTime()}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up after download starts
      setTimeout(() => {
        if (downloadUrl.startsWith("blob:")) {
          window.URL.revokeObjectURL(downloadUrl);
        }
        setDownloadUrl(null);
        setRejectedFileName("");
      }, 1000);
    }
  };

  // Clear form for CSV Upload
  const handleClear = () => {
    setFile(null);
    setMessage("");
    setMessageType("");
    setDownloadUrl(null);
    setRejectedFileName("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Clear form for CSV Filter
  const handleClear1 = () => {
    setFile1(null);
    setMessage1("");

    if (fileInputRef1.current) {
      fileInputRef1.current.value = "";
    }
  };

  return (
    <div className={classes.container}>
      <GridContainer justify="center">
        <GridItem xs={12} sm={10} md={8}>
          <Card>
            <CardBody>
              {/* CSV Filter Section */}
              <h4>CSV File Filter</h4>
              <p
                style={{
                  color: "#666",
                  fontSize: "14px",
                  marginBottom: "16px",
                }}
              >
                Upload a CSV to filter and download clean records
              </p>

              <div style={{ marginBottom: 16 }}>
                <input
                  ref={fileInputRef1}
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange1}
                  disabled={loading1}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: loading1 ? "#f5f5f5" : "#fff",
                    cursor: loading1 ? "not-allowed" : "pointer",
                  }}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <Button
                  color="primary"
                  onClick={handleUpload1}
                  disabled={!file1 || loading1}
                  style={{ marginRight: "10px" }}
                >
                  {loading1
                    ? "Processing..."
                    : "Upload & Download Filtered CSV"}
                </Button>

                {file1 && (
                  <Button
                    color="gray"
                    onClick={handleClear1}
                    disabled={loading1}
                  >
                    Clear
                  </Button>
                )}
              </div>

              {message1 && (
                <div
                  style={{
                    padding: "12px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    backgroundColor:
                      message1.toLowerCase().includes("success") ||
                      message1.toLowerCase().includes("downloaded")
                        ? "#d4edda"
                        : "#f8d7da",
                    color:
                      message1.toLowerCase().includes("success") ||
                      message1.toLowerCase().includes("downloaded")
                        ? "#155724"
                        : "#721c24",
                    marginBottom: "20px",
                  }}
                >
                  {message1}
                </div>
              )}

              {/* CSV Upload Section */}
              <h4 style={{ marginTop: "30px" }}>CSV File Upload</h4>
              <p
                style={{
                  color: "#666",
                  fontSize: "14px",
                  marginBottom: "16px",
                }}
              >
                Import contacts from CSV. Rejected records can be downloaded
                separately.
              </p>

              <div style={{ marginBottom: "20px" }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  disabled={uploading}
                  style={{
                    display: "block",
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: uploading ? "#f5f5f5" : "#fff",
                    cursor: uploading ? "not-allowed" : "pointer",
                  }}
                />
              </div>

              <div style={{ marginTop: "20px" }}>
                <Button
                  color="primary"
                  onClick={handleUpload}
                  disabled={uploading || !file}
                  style={{ marginRight: "10px" }}
                >
                  {uploading ? "Uploading..." : "Upload CSV"}
                </Button>

                {file && (
                  <Button
                    color="gray"
                    onClick={handleClear}
                    disabled={uploading}
                  >
                    Clear
                  </Button>
                )}
              </div>

              {message && (
                <p
                  style={{
                    color: messageType === "error" ? "#f44336" : "#4caf50",
                    marginTop: "15px",
                    padding: "10px",
                    backgroundColor:
                      messageType === "error" ? "#ffebee" : "#e8f5e9",
                    borderRadius: "4px",
                  }}
                >
                  {message}
                </p>
              )}

              {downloadUrl && (
                <div style={{ marginTop: "15px" }}>
                  <Button color="warning" onClick={handleDownload}>
                    Download Rejected Records
                  </Button>

                  {rejectedFileName && (
                    <span
                      style={{
                        marginLeft: "10px",
                        color: "#666",
                        fontSize: "14px",
                      }}
                    >
                      ({rejectedFileName})
                    </span>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
}
